import styled from '@emotion/styled';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';

// components
import Typography from '@components/_atoms/Typography';
import Button from '@components/_atoms/Button';

// constants
import { MOBILE_MODE } from '@constants/size';
import { gray, primary } from '@styles/Colors';
import { Radius } from '@styles/Radius';
import { ButtonColor, ButtonSize, TypoVariant } from '@constants/atoms';
import { searchDetailPath } from '@constants/routes/routes';

// type
import { ProductGetProductCategoryDataType } from 'models/SupplementType';
import { useTranslation } from 'next-i18next';
import { returnNumberWithComma } from '@utils/regex';
import { useState } from 'react';
import BasicModal from '@components/_atoms/Modal/Basic';
import Divider from '@components/_atoms/Divider';
import EtcForm from '../../EtcForm';
import EtcFormModal from '../../EtcForm/Modal';
import ButtonCode from '@constants/log/buttonCode';

type Props = {
  data: ProductGetProductCategoryDataType;
  product_type: number;
  standardCodeId: number;
  standardCode: string;
};

const Layout = styled.div`
  min-height: 260px;
  padding: 24px;
  border: 1px solid ${gray.gray4};
  border-radius: ${Radius.MEDIUM};

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media ${MOBILE_MODE} {
    min-height: 144px;
    padding: 16px;
  }
`;

const ContentBox = styled.div`
  @media ${MOBILE_MODE} {
    display: flex;
    column-gap: 16px;
  }
`;

const ImageWrapper = styled.div`
  width: 48px;
  height: 48px;
  position: relative;
`;

const DescriptionBox = styled.div`
  margin-top: 8px;

  display: grid;
  row-gap: 16px;

  @media ${MOBILE_MODE} {
    margin-top: 0;
    row-gap: 8px;
  }
`;

const ButtonWrapper = styled.div`
  @media ${MOBILE_MODE} {
    margin: 16px 0 0 64px;
  }
`;

export default function CategoryItem({ data, product_type, standardCodeId, standardCode }: Props) {
  const { t, i18n } = useTranslation('search');
  const productTypeId = Number(product_type);

  const ko = i18n.language === 'ko';
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { category, count, category_id, image_url, is_etc } = data as {
    category: string;
    count: number;
    category_id: number;
    image_url: string;
    is_etc: boolean;
  };
  const BUYER_SEARCH_DETAIL = searchDetailPath.query({
    product_type,
    category_id,
    category,
    standard_code: standardCode,
  });

  return (
    <Layout>
      <ContentBox>
        <ImageWrapper>
          <Image src={image_url} layout="fill" alt={category} />
        </ImageWrapper>

        <DescriptionBox>
          <Typography variant={TypoVariant.SH2} color={primary.gray}>
            {category}
          </Typography>
          {!is_etc ? (
            <Typography variant={TypoVariant.B4} color={primary.gray}>
              {returnNumberWithComma(count)} {t('count-description')}
            </Typography>
          ) : (
            <Typography variant={TypoVariant.B4} color={primary.gray}>
              {t('etc-placeholder')}
            </Typography>
          )}
        </DescriptionBox>
      </ContentBox>

      <ButtonWrapper>
        {/** TODO : Steelboso Id Add */}
        {is_etc ? (
          <Button
            width="100%"
            text={t('etc-item-button')}
            color={ButtonColor.OutlinedBlue}
            onClick={() => setIsModalOpen(true)}
          />
        ) : (
          <Link href={BUYER_SEARCH_DETAIL}>
            <a>
              <Button
                width="100%"
                text={t('item-button')}
                color={ButtonColor.OutlinedBlue}
                steelbosoId={ButtonCode.BTN_PRODUCT_READ}
              />
            </a>
          </Link>
        )}
      </ButtonWrapper>

      <EtcFormModal
        isOpen={isModalOpen}
        standardCodeId={standardCodeId}
        standardCode={standardCode}
        category_id={category_id}
        product_type_id={productTypeId}
        categoryData={data}
        onClose={() => setIsModalOpen(false)}
      />
    </Layout>
  );
}
